import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FolderTree,
  Search,
  Check,
  RefreshCw,
  Package,
  Store,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api, { admin, categories } from '@/lib/api';

const PAGE_SIZE = 100;

const BulkCategoryUpdate = () => {
  const [view, setView] = useState('sellers'); // sellers | products
  const [sellers, setSellers] = useState([]);
  const [sellersLoading, setSellersLoading] = useState(true);
  const [sellerSearch, setSellerSearch] = useState('');
  const [selectedSeller, setSelectedSeller] = useState(null);

  const [headerCategories, setHeaderCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productsTotal, setProductsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [targetCategoryName, setTargetCategoryName] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryStats, setCategoryStats] = useState({});

  useEffect(() => {
    let cancelled = false;
    categories
      .list()
      .then((res) => {
        const data = res.data;
        if (!cancelled) {
          setHeaderCategories(Array.isArray(data) ? data.filter((c) => c.is_active !== false) : []);
        }
      })
      .catch(() => {
        if (!cancelled) setHeaderCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchSellers = useCallback(async () => {
    try {
      setSellersLoading(true);
      const { data } = await api.get('/admin/sellers-with-products');
      if (data.success) {
        setSellers(data.sellers || []);
      }
    } catch (error) {
      console.error('Failed to fetch sellers:', error);
      toast.error('Bayi listesi alınamadı');
    } finally {
      setSellersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const fetchProducts = useCallback(async () => {
    if (!selectedSeller?.id) return;
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        seller_id: selectedSeller.id,
      };
      if (filterCategory && filterCategory !== 'all') {
        params.category = filterCategory;
      }
      if (searchQuery && searchQuery.trim().length >= 2) {
        params.search = searchQuery.trim();
      }
      const response = await admin.products(params);
      setProducts(response.data.products || []);
      setProductsTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Ürünler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedSeller, page, filterCategory, searchQuery]);

  useEffect(() => {
    if (view === 'products' && selectedSeller) {
      fetchProducts();
    }
  }, [view, selectedSeller, fetchProducts]);

  useEffect(() => {
    if (view !== 'products' || !selectedSeller?.id) return;
    const fetchStats = async () => {
      try {
        const response = await api.get('/admin/category-stats', {
          params: { seller_id: selectedSeller.id },
        });
        setCategoryStats(response.data || {});
      } catch (error) {
        console.error('Error fetching category stats:', error);
      }
    };
    fetchStats();
  }, [view, selectedSeller]);

  const sortedHeaderCategories = useMemo(
    () => [...headerCategories].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')),
    [headerCategories]
  );

  const filteredSellers = useMemo(() => {
    const q = sellerSearch.trim().toLowerCase();
    if (!q) return sellers;
    return sellers.filter(
      (s) =>
        (s.email || '').toLowerCase().includes(q) ||
        (s.name || '').toLowerCase().includes(q) ||
        (s.store_name || '').toLowerCase().includes(q)
    );
  }, [sellers, sellerSearch]);

  const totalPages = Math.max(1, Math.ceil(productsTotal / PAGE_SIZE));

  const openSeller = (seller) => {
    if (!seller.product_count) {
      toast.info('Bu bayinin ürünü yok');
      return;
    }
    setSelectedSeller(seller);
    setSelectedProducts([]);
    setTargetCategoryName('');
    setFilterCategory('all');
    setSearchQuery('');
    setPage(1);
    setCategoryStats({});
    setView('products');
  };

  const backToSellers = () => {
    setView('sellers');
    setSelectedSeller(null);
    setProducts([]);
    setSelectedProducts([]);
    setTargetCategoryName('');
    setFilterCategory('all');
    setSearchQuery('');
    setPage(1);
    fetchSellers();
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length && products.length > 0) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((p) => p.id));
    }
  };

  const handleBulkUpdate = async () => {
    if (!targetCategoryName) {
      toast.error('Lütfen hedef kategori seçin');
      return;
    }
    if (selectedProducts.length === 0) {
      toast.error('Lütfen en az bir ürün seçin');
      return;
    }

    setUpdating(true);
    try {
      const response = await api.post('/admin/products/bulk-category-update', {
        product_ids: selectedProducts,
        new_category: targetCategoryName,
      });

      toast.success(`${response.data.updated_count || selectedProducts.length} ürün güncellendi`);
      setSelectedProducts([]);
      setTargetCategoryName('');
      await fetchProducts();
      const statsRes = await api.get('/admin/category-stats', {
        params: { seller_id: selectedSeller.id },
      });
      setCategoryStats(statsRes.data || {});
    } catch (error) {
      console.error('Error updating categories:', error);
      toast.error(error.response?.data?.detail || 'Güncelleme sırasında hata oluştu');
    } finally {
      setUpdating(false);
    }
  };

  if (view === 'sellers') {
    return (
      <div className="space-y-6" data-testid="bayi-category-edit">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5" />
                  Bayi Kategori Düzenleme
                </CardTitle>
                <CardDescription className="mt-1">
                  Bir bayi seçin; ürünlerini işaretleyip istediğiniz vitrin kategorisine taşıyın.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchSellers} data-testid="refresh-sellers-btn">
                <RefreshCw className="w-4 h-4 mr-1" />
                Yenile
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Bayi ara (email, isim, mağaza)..."
                value={sellerSearch}
                onChange={(e) => setSellerSearch(e.target.value)}
                className="pl-10"
                data-testid="seller-search-input"
              />
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Bayi</TableHead>
                    <TableHead>Mağaza</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead className="text-center">Ürün Sayısı</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sellersLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-muted-foreground" />
                        Bayiler yükleniyor...
                      </TableCell>
                    </TableRow>
                  ) : filteredSellers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                        {sellerSearch ? 'Arama sonucu bulunamadı' : 'Bayi bulunamadı'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSellers.map((s) => (
                      <TableRow key={s.id} data-testid={`seller-row-${s.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{s.name || '—'}</p>
                            <p className="text-xs text-muted-foreground">{s.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{s.store_name || '—'}</TableCell>
                        <TableCell>
                          <Badge
                            variant={s.seller_status === 'approved' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {s.seller_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Package className="w-3.5 h-3.5 text-muted-foreground" />
                            <span
                              className={`font-bold ${s.product_count > 0 ? 'text-blue-600' : 'text-muted-foreground'}`}
                            >
                              {(s.product_count || 0).toLocaleString('tr-TR')}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!s.product_count}
                            onClick={() => openSeller(s)}
                            data-testid={`open-seller-${s.id}`}
                          >
                            Ürünleri Aç
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bayi-category-products">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="space-y-2">
              <Button variant="ghost" size="sm" className="gap-1 -ml-2 h-8" onClick={backToSellers}>
                <ArrowLeft className="w-4 h-4" />
                Bayilere dön
              </Button>
              <CardTitle className="flex items-center gap-2">
                <FolderTree className="w-5 h-5" />
                {selectedSeller?.store_name || selectedSeller?.name || selectedSeller?.email}
              </CardTitle>
              <CardDescription>
                {selectedSeller?.email} — ürün seçip hedef kategoriye aktarın.
                {productsTotal > 0 && (
                  <span className="ml-1 font-medium text-foreground">
                    ({productsTotal.toLocaleString('tr-TR')} ürün)
                  </span>
                )}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            {Object.entries(categoryStats).slice(0, 12).map(([cat, count]) => (
              <div
                key={cat}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  filterCategory === cat ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                }`}
                onClick={() => {
                  setPage(1);
                  setFilterCategory(cat === filterCategory ? 'all' : cat);
                }}
              >
                <div className="text-xs text-muted-foreground truncate">{cat}</div>
                <div className="text-lg font-bold">{count}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-[200px]">
              <Label>Kategori filtresi</Label>
              <Select
                value={filterCategory}
                onValueChange={(v) => {
                  setPage(1);
                  setFilterCategory(v);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tüm kategoriler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kategoriler</SelectItem>
                  {sortedHeaderCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 min-w-[200px]">
              <Label>Ürün ara</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="En az 2 karakter..."
                  value={searchQuery}
                  onChange={(e) => {
                    setPage(1);
                    setSearchQuery(e.target.value);
                  }}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          {selectedProducts.length > 0 && (
            <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg mb-4 flex-wrap">
              <Badge variant="secondary">{selectedProducts.length} seçili</Badge>
              <Select value={targetCategoryName} onValueChange={setTargetCategoryName}>
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Hedef kategori" />
                </SelectTrigger>
                <SelectContent>
                  {sortedHeaderCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleBulkUpdate} disabled={!targetCategoryName || updating} className="gap-2">
                {updating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Hedefe Aktar
              </Button>
            </div>
          )}

          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedProducts.length === products.length && products.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Ürün</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>category_id</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Yükleniyor...
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      Ürün bulunamadı
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedProducts.includes(product.id)}
                          onCheckedChange={() => handleSelectProduct(product.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {product.images?.[0] && (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-10 h-10 object-cover rounded"
                            />
                          )}
                          <div className="max-w-[280px]">
                            <div className="font-medium truncate">{product.name}</div>
                            <div className="text-xs text-muted-foreground">{product.id?.slice(0, 8)}...</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground font-mono">
                        {product.sku || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{product.category || '—'}</Badge>
                      </TableCell>
                      <TableCell
                        className="font-mono text-xs text-muted-foreground max-w-[100px] truncate"
                        title={product.category_id || ''}
                      >
                        {product.category_id ? `${product.category_id.slice(0, 8)}…` : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {productsTotal > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4 gap-4 flex-wrap">
              <p className="text-sm text-muted-foreground">
                Sayfa {page} / {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => {
                    setSelectedProducts([]);
                    setPage((p) => Math.max(1, p - 1));
                  }}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Önceki
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || loading}
                  onClick={() => {
                    setSelectedProducts([]);
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                >
                  Sonraki
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkCategoryUpdate;
